import { cn } from '@/lib/utils'
import { RowModel } from '@tanstack/react-table'
import { useState } from 'react'
import { useTableApiContext } from '../../utils/table-context'
import { GridColDefPro, GridRowId, ValidRowModel } from '../../utils/types'

const commonClasses = cn(
        'cursor-text size-full text-primary bg-transparent border-none flex items-center',
        'hover:bg-[rgba(0,_0,_0,_0.25)] overflow-hidden min-w-0 px-1 rounded-sm',
    ),
    inheritedStyles: React.CSSProperties = {
        fontSize: 'inherit',
        fontFamily: 'inherit',
        fontWeight: 'inherit',
        maxHeight: 'calc(100% - 2px)',
        color: 'inherit',
    }

interface IPropsEditableCell<TData extends ValidRowModel = any> {
    type: GridColDefPro['type']
    defaultValue: any
    field: string
    id: GridRowId
    row: RowModel<TData>
}

export default function EditableCell({
    type,
    defaultValue,
    field,
    id,
    row,
}: IPropsEditableCell) {
    const { onCellEditCommit } = useTableApiContext()
    const [isEditing, setEditing] = useState(false),
        [value, setValue] = useState(defaultValue)

    const inputType = (() => {
        switch (type) {
            case 'date':
            case 'datetime-local':
            case 'time':
            case 'number':
                return type
            case 'SN':
            case 'boolean':
                return 'checkbox'
            default:
                return 'text'
        }
    })()

    if (!isEditing)
        return (
            <input
                readOnly
                type={inputType}
                value={value}
                placeholder=" "
                className={cn(commonClasses, 'outline-none')}
                style={inheritedStyles}
                onDoubleClick={() => setEditing(true)}
                onFocus={() => setEditing(true)}
            />
        )

    function onCommit() {
        setEditing(false)
        if (isEditing && value != defaultValue)
            onCellEditCommit?.({
                id,
                field,
                value,
                row,
            })
    }

    return (
        <EditableCellDefault
            {...{
                inputType,
                onCommit,
                value,
                setValue,
            }}
        />
    )
}

interface IPropsDefaultEditableCellDefault {
    inputType: string
    value: IPropsEditableCell['defaultValue']
    setValue: React.Dispatch<React.SetStateAction<any>>
    onCommit: () => void
}

function EditableCellDefault({
    inputType,
    onCommit,
    value,
    setValue,
}: IPropsDefaultEditableCellDefault) {
    const previousValue = structuredClone(value)

    return (
        <input
            autoFocus
            type={inputType}
            onBlur={onCommit}
            onKeyDown={e => {
                if (e.key === 'Enter') onCommit()
                if (e.key === 'Escape') {
                    e.preventDefault()
                    e.stopPropagation()
                    setValue(previousValue)
                    onCommit()
                }
            }}
            value={value}
            onChange={e => {
                setValue(e.target.value)
            }}
            className={cn(commonClasses, 'bg-[rgba(0,_0,_0,_0.25)] outline-1')}
            style={inheritedStyles}
        />
    )
}
